import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pages } from '../../Pages'
import LoadingSpinner from '../common/loading/LoadingSpinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import Button from '../../components/Button'
import { AuthHelper } from '../../auth/AuthHelper'
import { consumeInviteTarget } from '../../auth/inviteTarget'
import { useAuth } from '../../auth/AuthContext'
import authApi from '../../api/auth/AuthApi'
import MagicLinkNotification from './MagicLinkNotification'
import EmailLinkButton from './EmailLinkButton'
import { MAGIC_LINK_ERROR_FLAG, MAGIC_LINK_ERROR_MESSAGE } from './constants'
import { executeRecaptcha } from '../../utils/recaptcha'

enum LoginStep {
    Email = 'email',
    ChooseMethod = 'choose-method',
    NoAccount = 'no-account',
    MagicSent = 'magic-sent',
}

export default function Login(): React.JSX.Element {
    const { user, loading, error, loginWithPassword, requestMagicLink } =
        useAuth()
    const navigate = useNavigate()
    const [email, setEmail] = useState(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('email') ?? ''
    })
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [status, setStatus] = useState<string | null>(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('magicError') === MAGIC_LINK_ERROR_FLAG
            ? MAGIC_LINK_ERROR_MESSAGE
            : null
    })
    const [missingEmail, setMissingEmail] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [magicLinkMessage, setMagicLinkMessage] = useState<string | null>(
        null,
    )
    const [step, setStep] = useState<LoginStep>(() => {
        const params = new URLSearchParams(window.location.search)
        const stepParam = params.get('step')
        const emailParam = params.get('email')
        if (stepParam === LoginStep.ChooseMethod && emailParam) {
            return LoginStep.ChooseMethod
        }
        return LoginStep.Email
    })

    // Parse optional target query param produced by password reset result_url
    const targetPath = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        const raw = params.get('target')
        let source: string | undefined | null = raw
        if (!source) {
            source = consumeInviteTarget()
        }
        if (!source) return undefined
        let decoded: string
        try {
            decoded = decodeURIComponent(source)
        } catch {
            return undefined
        }
        // Security: only allow relative in-app paths
        if (!decoded.startsWith('/') || decoded.startsWith('//'))
            return undefined
        return decoded
    }, [])

    const autoRedirectHandled = useRef(false)

    useEffect(() => {
        if (step === LoginStep.ChooseMethod && !email) {
            setStep(LoginStep.Email)
        }
    }, [step, email])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (!params.has('magicError')) return
        params.delete('magicError')
        const query = params.toString()
        const newUrl = `${window.location.pathname}${
            query ? `?${query}` : ''
        }${window.location.hash}`
        window.history.replaceState(null, '', newUrl)
    }, [])

    useEffect(() => {
        if (!loading && user && !autoRedirectHandled.current) {
            autoRedirectHandled.current = true
            navigate(targetPath || Pages.Roles, { replace: true })
        }
    }, [loading, user, targetPath, navigate])

    const handlePasswordLogin = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()

            if (!email || !password) {
                setStatus('Please provide both email and password.')
                return
            }
            try {
                setSubmitting(true)
                setStatus(null)
                setMissingEmail(null)
                AuthHelper.removeTenantOverrideId()
                const recaptchaToken = await executeRecaptcha('password_login')
                await loginWithPassword(email, password, recaptchaToken)
                navigate(targetPath || Pages.Roles, { replace: true })
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to log in. Please try again.'
                setStatus(
                    message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    ),
                )
                setMissingEmail(extractMissingEmail(message))
            } finally {
                setSubmitting(false)
            }
        },
        [email, password, loginWithPassword, navigate, targetPath],
    )

    const sendMagicLink = useCallback(async () => {
        if (!email) {
            setStatus('Please enter your email to receive a magic link.')
            return
        }
        try {
            setSubmitting(true)
            setMissingEmail(null)
            AuthHelper.removeTenantOverrideId()
            const recaptchaToken = await executeRecaptcha('request_magic_link')
            await requestMagicLink(email, recaptchaToken, targetPath)
            setStep(LoginStep.MagicSent)
            setStatus(null)
            setMagicLinkMessage(`We have sent a magic link to ${email}`)
        } catch (err: unknown) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Unable to send magic link. Please try again.'
            setStatus(
                message.replace(
                    /^There was an issue communicating with the Contxt server:\s*/,
                    '',
                ),
            )
            setMissingEmail(extractMissingEmail(message))
            setMagicLinkMessage(null)
        } finally {
            setSubmitting(false)
        }
    }, [email, requestMagicLink, targetPath])

    const handleEmailChange = useCallback((value: string) => {
        setEmail(value)
        setMissingEmail(null)
        setStatus(null)
        setMagicLinkMessage(null)
        setPassword('')
    }, [])

    const handleSelectMagicLink = useCallback(() => {
        if (submitting) return
        sendMagicLink()
    }, [sendMagicLink, submitting])

    const handleEmailSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            const trimmedEmail = email.trim()
            if (!trimmedEmail) {
                setStatus('Please enter your email to continue.')
                setMissingEmail(null)
                return
            }

            setEmail(trimmedEmail)
            setStatus(null)
            setMissingEmail(null)
            setPassword('')

            try {
                setSubmitting(true)
                const recaptchaToken = await executeRecaptcha('check_email')
                const response = await authApi.checkEmailExists(
                    trimmedEmail,
                    recaptchaToken,
                )
                if (response.exists) {
                    setStep(LoginStep.ChooseMethod)
                } else {
                    setStep(LoginStep.NoAccount)
                }
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to verify this email. Please try again.'
                setStatus(
                    message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    ),
                )
                setStep(LoginStep.Email)
            } finally {
                setSubmitting(false)
            }
        },
        [email],
    )

    const handleEditEmail = useCallback(() => {
        setStep(LoginStep.Email)
        setPassword('')
        setStatus(null)
        setMissingEmail(null)
        setMagicLinkMessage(null)
    }, [])

    if (loading && !user) {
        return <LoadingSpinner />
    }

    if (step === LoginStep.MagicSent) {
        return (
            <MagicLinkNotification
                email={email}
                subtitle={
                    magicLinkMessage ||
                    `We sent a magic link to ${email}. Use it to sign back in.`
                }
                onUseDifferentEmail={() => {
                    setStep(LoginStep.Email)
                    setMagicLinkMessage(null)
                }}
            />
        )
    }

    const showMarketing = step === LoginStep.Email

    return (
        <div
            className={
                showMarketing
                    ? 'flex flex-col md:flex-row min-h-screen items-center justify-center bg-gray-50 p-6 md:items-stretch md:justify-start md:bg-transparent md:p-0'
                    : 'min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6'
            }
        >
            {showMarketing && (
                <div className="hidden md:flex flex-1 bg-gradient-to-br from-ctxt-blue via-gray-900 to-gray-800 text-white flex-col justify-center items-center p-8">
                    <div className="max-w-md text-center space-y-4">
                        <img src="Contxt_Logo_Full_WHT.png" />
                        <h1 className="text-2xl md:text-2xl mb-2 opacity-90">
                            Top performers don't apply...
                        </h1>
                        <h1 className="text-3xl md:text-4xl font-bold opacity-95">
                            They move through trusted networks
                        </h1>
                    </div>
                </div>
            )}

            <div
                className={
                    showMarketing
                        ? 'w-full max-w-md mx-auto bg-white shadow-lg rounded-lg p-8 flex flex-col justify-center items-center md:max-w-none md:mx-0 md:flex-1 md:bg-white md:flex md:flex-col md:justify-center md:items-center md:p-6 md:shadow-none md:rounded-none'
                        : 'w-full max-w-md bg-white shadow-lg rounded-lg p-8'
                }
            >
                <div className="w-full max-w-sm space-y-6 text-center mx-auto">
                    {showMarketing && (
                        <div className="space-y-2 md:hidden">
                            <img
                                src="Contxt_Logo_Full_BLK_CROPPED.png"
                                className="w-40 mx-auto"
                                alt="Contxt"
                            />
                        </div>
                    )}
                    {!showMarketing && (
                        <div className="space-y-2">
                            <img
                                src="Contxt_Logo_Full_BLK_CROPPED.png"
                                className="w-40 mx-auto"
                                alt="Contxt"
                            />
                        </div>
                    )}
                    {step === LoginStep.Email && (
                        <>
                            <h2 className="text-2xl font-bold">
                                Welcome Back!
                            </h2>
                            <p className="text-gray-500">
                                Access your dashboard and discover top
                                performers in your network
                            </p>
                            <form
                                className="space-y-4"
                                onSubmit={handleEmailSubmit}
                            >
                                <input
                                    type="email"
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) =>
                                        handleEmailChange(e.target.value)
                                    }
                                    required
                                />
                                <Button
                                    style="primary"
                                    type="submit"
                                    classes="w-full"
                                    enabled={!submitting}
                                >
                                    Continue
                                </Button>
                            </form>
                        </>
                    )}

                    {step === LoginStep.ChooseMethod && (
                        <div className="space-y-6 text-left">
                            <div className="space-y-1 text-center">
                                <p className="text-sm font-medium text-gray-700">
                                    Sign in with <strong>{email}</strong>
                                </p>
                            </div>

                            <EmailLinkButton
                                sendingLink={submitting}
                                handleSendMagicLink={handleSelectMagicLink}
                            />

                            <div className="space-y-4 border-t border-gray-200 pt-4">
                                <p className="text-sm font-semibold text-gray-800">
                                    Or use your password
                                </p>
                                <form
                                    className="space-y-4"
                                    onSubmit={handlePasswordLogin}
                                >
                                    <div className="relative">
                                        <input
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            className="w-full border border-gray-300 rounded px-3 py-2 pr-10"
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value)
                                                setStatus(null)
                                            }}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                                            onClick={() =>
                                                setShowPassword((prev) => !prev)
                                            }
                                            aria-label={
                                                showPassword
                                                    ? 'Hide password'
                                                    : 'Show password'
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    showPassword
                                                        ? faEyeSlash
                                                        : faEye
                                                }
                                            />
                                        </button>
                                    </div>
                                    <Button
                                        style="secondary"
                                        type="submit"
                                        classes="w-full"
                                        enabled={!submitting}
                                    >
                                        Log In
                                    </Button>
                                </form>
                            </div>

                            <div className="text-center">
                                <button
                                    type="button"
                                    className="text-sm text-blue-600 hover:underline"
                                    onClick={handleEditEmail}
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    )}

                    {step === LoginStep.NoAccount && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                We can't find an account for{' '}
                                <strong>{email}</strong>
                            </p>
                            <Button
                                style="primary"
                                type="button"
                                classes="w-full"
                                enabled={!submitting}
                                onClick={() =>
                                    navigate(
                                        `${
                                            Pages.Signup
                                        }?email=${encodeURIComponent(
                                            email,
                                        )}&step=choose-method`,
                                    )
                                }
                            >
                                Create a new Contxt account
                            </Button>
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleEditEmail}
                            >
                                Use a different email
                            </button>
                        </div>
                    )}

                    <div className="flex flex-col space-y-2">
                        {status && !missingEmail && (
                            <p className="text-sm text-red-600 whitespace-pre-wrap">
                                {status}
                            </p>
                        )}
                        {missingEmail && (
                            <div className="text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded px-3 py-2 space-y-2">
                                <p className="text-red-600">
                                    We can't find an account for{' '}
                                    <strong>{missingEmail}</strong>
                                </p>
                                <Button
                                    style="secondary"
                                    type="button"
                                    classes="w-full"
                                    enabled={!submitting}
                                    onClick={() =>
                                        navigate(
                                            `${
                                                Pages.Signup
                                            }?email=${encodeURIComponent(
                                                missingEmail,
                                            )}&step=choose-method`,
                                        )
                                    }
                                >
                                    Create a new Contxt account
                                </Button>
                            </div>
                        )}
                        {error && (
                            <p className="text-sm text-red-600 whitespace-pre-wrap">
                                {error}
                            </p>
                        )}
                    </div>

                    {step === LoginStep.Email && (
                        <p className="text-sm text-gray-500">
                            New to Contxt?{' '}
                            <Link
                                to={Pages.Signup}
                                className="text-blue-600 hover:underline"
                            >
                                Create a new account
                            </Link>
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

function extractMissingEmail(message: string): string | null {
    const match = message.match(/User with email "([^"]+)" not found/i)
    return match ? match[1] : null
}
